import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../config';

import {
  ArrowsClockwise,
  Check,
  Clock,
  FileText,
  FolderOpen,
  GearSix,
  HardDrive,
  Trash,
  WarningCircle,
  X
} from '@phosphor-icons/react';
/**
 * WorkspaceSettings - GearSix → Files Section
 * 
 * Allows users to:
 * - View/change workspace location
 * - Configure cleanup rules
 * - Manually trigger cleanup
 */
export const WorkspaceSettings = ({ agentId }) => {
  const [settings, setSettings] = useState(null);
  const [resolvedPath, setResolvedPath] = useState(null);
  const [cleanupStatus, setCleanupStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  
  // Form state
  const [useDefault, setUseDefault] = useState(true);
  const [customPath, setCustomPath] = useState('');
  const [pathValid, setPathValid] = useState(null);
  const [cleanupEnabled, setCleanupEnabled] = useState(true);
  const [retentionDays, setRetentionDays] = useState(7);

  // Fetch current settings
  const fetchSettings = useCallback(async () => {
    try {
      const [settingsRes, resolvedRes, cleanupRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/exec/workspace/settings?agent_id=${agentId}`, {
          credentials: 'include',
        }),
        fetch(`${BACKEND_URL}/api/exec/workspace/resolve/${agentId}`, {
          credentials: 'include',
        }),
        fetch(`${BACKEND_URL}/api/exec/workspace/cleanup/status?agent_id=${agentId}`, {
          credentials: 'include',
        }),
      ]);

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(data);
        setUseDefault(data.use_default ?? true);
        setCustomPath(data.custom_path || '');
        setCleanupEnabled(data.cleanup_enabled ?? true);
        setRetentionDays(data.cleanup_retention_days || 7);
      }

      if (resolvedRes.ok) {
        const data = await resolvedRes.json();
        setResolvedPath(data);
      }

      if (cleanupRes.ok) {
        const data = await cleanupRes.json();
        setCleanupStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch workspace settings:', error);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Validate custom path
  const validatePath = async (path) => {
    if (!path.trim()) {
      setPathValid(null);
      return;
    }

    setValidating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/workspace/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ path }),
      });

      if (response.ok) {
        const data = await response.json();
        setPathValid(data.valid);
      } else {
        setPathValid(false);
      }
    } catch (error) {
      console.error('Path validation failed:', error);
      setPathValid(false);
    } finally {
      setValidating(false);
    }
  };

  // Save settings
  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/workspace/settings?agent_id=${agentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          use_default: useDefault,
          custom_path: useDefault ? null : customPath,
          cleanup_enabled: cleanupEnabled,
          cleanup_retention_days: retentionDays,
        }),
      });

      if (response.ok) {
        toast.success('Workspace settings saved');
        fetchSettings(); // ArrowsClockwise
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Trigger cleanup
  const runCleanup = async () => {
    setCleaningUp(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/workspace/cleanup/run?agent_id=${agentId}`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Cleanup complete: ${result.deleted_count} files removed (${result.freed_space})`);
        fetchSettings(); // ArrowsClockwise status
      } else {
        toast.error('Cleanup failed');
      }
    } catch (error) {
      console.error('Cleanup failed:', error);
      toast.error('Cleanup failed');
    } finally {
      setCleaningUp(false);
    }
  };

  if (loading) {
    return (
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-center py-8">
          <ArrowsClockwise className="animate-spin text-pink-500" size={24} />
        </div>
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl p-6" data-testid="workspace-settings">
      <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
        <FolderOpen size={24} className="text-pink-500" />
        File Workspace
      </h2>
      
      <p className="text-[#B0B8C4] text-sm mb-6">
        Configure where Orion saves files when executing tasks. Files are organized by date for easy management.
      </p>

      {/* Current Workspace Path */}
      <div className="mb-6 p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
        <div className="flex items-center gap-3 mb-2">
          <HardDrive size={18} className="text-blue-400" />
          <span className="text-white font-medium">Current Workspace</span>
          {resolvedPath?.is_fallback && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400">
              Fallback
            </span>
          )}
        </div>
        <code className="text-sm text-[#B0B8C4] break-all">
          {resolvedPath?.resolved_path || 'Not resolved'}
        </code>
      </div>

      {/* Workspace Location Setting */}
      <div className="space-y-4 mb-6">
        <label className="text-[#F0F4F8] text-sm font-medium">Workspace Location</label>
        
        {/* Default Option */}
        <div 
          className={`p-4 rounded-xl cursor-pointer transition-all ${
            useDefault 
              ? 'bg-[#00E5C0]/10 border border-[#00E5C0]/30' 
              : 'bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.05)]'
          }`}
          onClick={() => {
            setUseDefault(true);
            setPathValid(null);
          }}
        >
          <div className="flex items-center gap-3">
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              useDefault ? 'border-[#00E5C0] bg-[#00E5C0]' : 'border-zinc-600'
            }`}>
              {useDefault && <Check size={12} className="text-white" />}
            </div>
            <div>
              <span className="text-white font-medium">Default Location</span>
              <span className="text-[#00E5C0] text-xs ml-2">(Recommended)</span>
              <p className="text-[#7D8896] text-sm mt-1">
                Uses OS-specific Orion folder (Documents/Orion or system temp)
              </p>
            </div>
          </div>
        </div>

        {/* Custom Option */}
        <div 
          className={`p-4 rounded-xl transition-all ${
            !useDefault 
              ? 'bg-[#00E5C0]/10 border border-[#00E5C0]/30' 
              : 'bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]'
          }`}
        >
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => setUseDefault(false)}
          >
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              !useDefault ? 'border-[#00E5C0] bg-[#00E5C0]' : 'border-zinc-600'
            }`}>
              {!useDefault && <Check size={12} className="text-white" />}
            </div>
            <div>
              <span className="text-white font-medium">Custom Location</span>
              <p className="text-[#7D8896] text-sm mt-1">
                Specify your own folder path
              </p>
            </div>
          </div>
          
          {!useDefault && (
            <div className="mt-4 ml-8">
              <div className="relative">
                <input
                  type="text"
                  value={customPath}
                  onChange={(e) => {
                    setCustomPath(e.target.value);
                    setPathValid(null);
                  }}
                  onBlur={() => validatePath(customPath)}
                  placeholder="e.g., ~/Documents/MyOrionFiles"
                  className="w-full px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-zinc-500 focus:outline-none focus:border-[#00E5C0]/50"
                  data-testid="custom-path-input"
                />
                {validating && (
                  <ArrowsClockwise size={16} className="absolute right-3 top-3 text-[#7D8896] animate-spin" />
                )}
                {pathValid === true && (
                  <Check size={16} className="absolute right-3 top-3 text-[#00E5C0]" />
                )}
                {pathValid === false && (
                  <X size={16} className="absolute right-3 top-3 text-[#FF6B6B]" />
                )}
              </div>
              {pathValid === false && (
                <p className="text-[#FF6B6B] text-xs mt-1 flex items-center gap-1">
                  <WarningCircle size={12} />
                  Path is not writable or doesn't exist
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Cleanup GearSix */}
      <div className="border-t border-[rgba(255,255,255,0.05)] pt-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Trash size={18} className="text-amber-400" />
          <span className="text-white font-medium">Auto-Cleanup</span>
        </div>
        
        <div className="flex items-center justify-between p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Clock size={20} className="text-amber-400" />
            </div>
            <div>
              <h3 className="text-white font-medium">Delete old temporary files</h3>
              <p className="text-[#7D8896] text-sm">Automatically clean up files older than:</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={retentionDays}
              onChange={(e) => setRetentionDays(parseInt(e.target.value))}
              disabled={!cleanupEnabled}
              className="px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:outline-none focus:border-[#00E5C0]/50 disabled:opacity-50"
              data-testid="retention-days-select"
            >
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
            
            <button
              onClick={() => setCleanupEnabled(!cleanupEnabled)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                cleanupEnabled
                  ? 'bg-green-500/20 text-[#00E5C0] hover:bg-green-500/30'
                  : 'bg-zinc-500/20 text-[#B0B8C4] hover:bg-zinc-500/30'
              }`}
              data-testid="cleanup-toggle"
            >
              {cleanupEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>
        </div>

        {/* Cleanup Status */}
        {cleanupStatus?.last_cleanup && (
          <div className="mt-4 p-3 rounded-lg bg-[rgba(255,255,255,0.02)] text-[#B0B8C4] text-sm">
            <div className="flex items-center gap-2">
              <FileText size={14} />
              <span>Last cleanup: {new Date(cleanupStatus.last_cleanup.completed_at).toLocaleString()}</span>
              <span className="text-[#7D8896]">•</span>
              <span>{cleanupStatus.last_cleanup.deleted_count} files removed</span>
              <span className="text-[#7D8896]">•</span>
              <span>{cleanupStatus.last_cleanup.freed_space} freed</span>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-[rgba(255,255,255,0.05)]">
        <button
          onClick={runCleanup}
          disabled={cleaningUp}
          className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-all disabled:opacity-50 flex items-center gap-2"
          data-testid="run-cleanup-btn"
        >
          {cleaningUp ? (
            <ArrowsClockwise size={16} className="animate-spin" />
          ) : (
            <Trash size={16} />
          )}
          {cleaningUp ? 'Cleaning...' : 'Clean Up Now'}
        </button>

        <button
          onClick={saveSettings}
          disabled={saving || (!useDefault && pathValid === false)}
          className="px-6 py-2 rounded-lg bg-gradient-to-r from-[#00E5C0] to-purple-600 text-white font-medium hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <ArrowsClockwise size={16} className="animate-spin" />
          ) : (
            <Check size={16} />
          )}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default WorkspaceSettings;
